import React, { useState } from 'react';
import CommonInput from '../../Common/CommonInput';
import CommonSelect from '../../Common/CommonSelect';
import InlineFormRow from './InlineFormRow';
import MaskedField from './MaskedField';
import zohoService from '../../../services/zohoService';
import { toast } from 'react-toastify';

const SECRET_UNCHANGED = '__UNCHANGED__';

/**
 * Compact API Configuration card.
 * Absorbs the old ZohoConnectionCard (status + Disconnect / Authorize button).
 * Fields laid out 2-per-row using InlineFormRow (label + input same line).
 */
const ZohoApiConfigCard = ({
  formData,
  setFormData,
  dataCenterOptions,
  errors,
  setErrors,
  onSaveCredentials,
  onRefresh,
  isLoading,
  /** Props originally on ZohoConnectionCard — now merged here. */
  isConnected,
  expires,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [secretEdited, setSecretEdited] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'zoho_client_secret') {
      setSecretEdited(true);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAutoSaveDropdown = async (field, value, previousValue, payload, successMessage) => {
    const prevId = previousValue?.id ?? previousValue;
    const nextId = value?.id ?? value;

    if (prevId === nextId) {
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    setSavingField(field);

    try {
      await zohoService.savePartialSettings(payload);
      toast.success(successMessage, { position: 'top-right', autoClose: 2000 });
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setFormData((prev) => ({ ...prev, [field]: previousValue }));
      toast.error(error.message || 'Failed to save setting', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setSavingField(null);
    }
  };

  const handleDataCenterChange = (value) => {
    handleAutoSaveDropdown(
      'zoho_data_center',
      value,
      formData.zoho_data_center,
      { zoho_data_center: value?.id },
      'Data center saved'
    );
  };

  const handleStatusChange = (value) => {
    handleAutoSaveDropdown(
      'zoho_status',
      value,
      formData.zoho_status,
      { zoho_status: value },
      'Status saved'
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      const payload = {
        zoho_client_id: formData.zoho_client_id,
        zoho_data_center: formData.zoho_data_center?.id || formData.zoho_data_center,
        zoho_status: formData.zoho_status,
        zoho_client_secret: secretEdited
          ? formData.zoho_client_secret
          : (formData.zoho_client_secret ? SECRET_UNCHANGED : formData.zoho_client_secret),
      };
      await onSaveCredentials(payload);
      setSecretEdited(false);
      setErrors({});
    } catch (error) {
      if (error.errors) {
        const newErrors = {};
        Object.keys(error.errors).forEach((key) => {
          newErrors[key] = Array.isArray(error.errors[key])
            ? error.errors[key][0]
            : error.errors[key];
        });
        setErrors(newErrors);
      } else {
        setErrors({ general: error.message || 'An error occurred while saving' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyRedirectUri = async () => {
    try {
      await navigator.clipboard.writeText(formData.zoho_redirect_uri);
      toast.success('Redirect URL copied to clipboard', {
        position: 'top-right',
        autoClose: 2000,
      });
    } catch {
      toast.error('Failed to copy redirect URL', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleAuthorize = async () => {
    try {
      setIsAuthorizing(true);
      const url = await zohoService.getAuthorizeUrl();
      window.location.href = url;
    } catch (error) {
      toast.error(error.message || 'Error starting Zoho authorization', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsAuthorizing(false);
    }
  };

  const handleDisconnectConfirm = async () => {
    try {
      setIsDisconnecting(true);
      await zohoService.disconnect();
      toast.success('Zoho disconnected successfully', {
        position: 'top-right',
        autoClose: 3000,
      });
      setShowDisconnectModal(false);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to disconnect Zoho', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const busy = isLoading || isSaving || savingField !== null || isAuthorizing || isDisconnecting;

  return (
    <>
      <div className="card mb-3">
        <div className="card-body py-2 px-3">
          {/* Header: title + connection status + Disconnect/Authorize button */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="card-title mb-0 d-inline-flex align-items-center gap-2">
                API Configuration
                <span className={`badge ${isConnected ? 'bg-success' : 'bg-secondary'}`}>
                  {isConnected ? 'Connected' : 'Not connected'}
                </span>
              </h5>
              {isConnected && expires && (
                <small className="text-muted ms-2">
                  Token expires: {expires}
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              {!isConnected ? (
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={handleAuthorize}
                  disabled={busy}
                >
                  Authorize with Zoho
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={busy}
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>

          <p className="text-muted small mb-3">
            Create these credentials in the Zoho API Console, then save before authorizing.
          </p>

          <form onSubmit={handleSubmit}>
            {/* 2-column grid */}
            <div className="row g-2">
              {/* Left column */}
              <div className="col-md-6">
                <InlineFormRow label="Client ID" required={true} error={errors?.zoho_client_id}>
                  <CommonInput
                    name="zoho_client_id"
                    value={formData.zoho_client_id}
                    onChange={handleChange}
                    errors={errors}
                    displayError={false}
                    className=""
                  />
                </InlineFormRow>

                <InlineFormRow label="Client Secret" required={!formData.zoho_client_secret}>
                  <MaskedField
                    name="zoho_client_secret"
                    value={formData.zoho_client_secret}
                    onChange={handleChange}
                    errors={errors}
                    required={!formData.zoho_client_secret}
                    compact={true}
                  />
                  {errors?.zoho_client_secret && (
                    <div className="text-danger" style={{ fontSize: '0.6875rem', marginTop: 2 }}>
                      {Array.isArray(errors.zoho_client_secret)
                        ? errors.zoho_client_secret[0]
                        : errors.zoho_client_secret}
                    </div>
                  )}
                </InlineFormRow>

                <InlineFormRow
                  label="Redirect URL"
                  required={true}
                  hint="Register this exact URL in your Zoho API Console."
                  labelWidth={130}
                >
                  <div className="input-group">
                    <input
                      type="text"
                      name="zoho_redirect_uri"
                      className="form-control form-control-sm"
                      value={formData.zoho_redirect_uri}
                      disabled
                      readOnly
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleCopyRedirectUri}
                      title="Copy to clipboard"
                    >
                      <i className="fa fa-copy" />
                    </button>
                  </div>
                </InlineFormRow>
              </div>

              {/* Right column */}
              <div className="col-md-6">
                <InlineFormRow label="Data Center" required={true} labelWidth={130}>
                  <CommonSelect
                    name="zoho_data_center"
                    value={formData.zoho_data_center}
                    onChange={handleDataCenterChange}
                    options={dataCenterOptions}
                    errors={errors}
                    required={true}
                    isClearable={false}
                    isSubmitting={savingField === 'zoho_data_center'}
                    className=""
                  />
                </InlineFormRow>

                <InlineFormRow label="Status" required={true} labelWidth={130}>
                  <CommonSelect
                    name="zoho_status"
                    value={formData.zoho_status}
                    onChange={handleStatusChange}
                    options={[
                      { label: 'Active', id: '1' },
                      { label: 'Disabled', id: '0' },
                    ]}
                    errors={errors}
                    required={true}
                    isClearable={false}
                    isSubmitting={savingField === 'zoho_status'}
                    className=""
                  />
                </InlineFormRow>
              </div>
            </div>

            {errors.general && (
              <div className="alert alert-danger py-2 mt-2">{errors.general}</div>
            )}

            <div className="d-flex justify-content-end mt-2">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={busy}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Disconnect confirmation modal */}
      {showDisconnectModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h5 className="modal-title fs-6">Disconnect Zoho Books</h5>
                <button type="button" className="btn-close" onClick={() => setShowDisconnectModal(false)} />
              </div>
              <div className="modal-body small">
                <p className="mb-0">
                  This will revoke access at Zoho and remove stored tokens.
                  You will need to re-authorize to sync with Zoho Books again.
                </p>
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDisconnectModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleDisconnectConfirm}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ZohoApiConfigCard;
